/**
 * Browser-native wake-word detector using Web Speech API.
 * Listens continuously for phrases containing "hey glitch" or "hi glitch"
 * and fires a callback. Uses low-power interim results.
 * Returns { start, stop, supported }.
 */

const isSupported = () =>
  typeof window !== "undefined" &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

const WAKE_PATTERNS = [
  /\bhey[\s,]+glitch\b/i,
  /\bhi[\s,]+glitch\b/i,
  /\byo[\s,]+glitch\b/i,
  /\bok(?:ay)?[\s,]+glitch\b/i,
];

export const createWakeWordListener = ({ onDetected, onError } = {}) => {
  const SR =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;

  let rec = null;
  let active = false;
  let restartTimer = null;
  let stopping = false;

  const buildRec = () => {
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-US";
    r.maxAlternatives = 1;

    r.onresult = (event) => {
      const last = event.results[event.results.length - 1];
      const transcript = (last && last[0] && last[0].transcript) || "";
      if (WAKE_PATTERNS.some((re) => re.test(transcript))) {
        try {
          r.stop();
        } catch (_) {
          /* ignore */
        }
        onDetected?.(transcript.trim());
      }
    };

    r.onerror = (e) => {
      // "no-speech" / "aborted" are expected; only escalate real errors
      if (e && e.error && !["no-speech", "aborted", "audio-capture"].includes(e.error)) {
        onError?.(e.error);
      }
    };

    r.onend = () => {
      if (active && !stopping) {
        // auto-restart to keep continuous listening
        restartTimer = setTimeout(() => {
          try {
            r.start();
          } catch (_) {
            /* ignore already-started */
          }
        }, 250);
      }
    };
    return r;
  };

  const start = () => {
    if (!SR || active) return;
    active = true;
    stopping = false;
    rec = buildRec();
    try {
      rec.start();
    } catch (_) {
      /* ignore */
    }
  };

  const stop = () => {
    active = false;
    stopping = true;
    if (restartTimer) {
      clearTimeout(restartTimer);
      restartTimer = null;
    }
    if (rec) {
      try {
        rec.stop();
      } catch (_) {
        /* ignore */
      }
      try {
        rec.abort();
      } catch (_) {
        /* ignore */
      }
      rec = null;
    }
  };

  return { start, stop, supported: !!SR };
};

export const wakeWordSupported = () => !!isSupported();
